<!--
  LoginView.vue — 登录页
  居中卡片式登录表单，包含用户名/密码输入与表单验证。
  登录成功后将 token 和 username 存入 localStorage 并跳转至控制台。
  注意：当前使用模拟登录逻辑（硬编码验证），需替换为真实接口调用。
-->
<template>
  <div class="login-container">
    <div class="login-box">
      <div class="login-header">
        <div class="login-lottie-wrap">
          <LottieIcon
            :animation-data="runOxAnimation"
            :size="50"
          />
        </div>
        <h1 class="login-title">数据统计平台</h1>
      </div>
      <a-form
        :model="formState"
        :rules="rules"
        ref="formRef"
        layout="vertical"
        @finish="handleLogin"
      >
        <a-form-item
          name="username"
          label="用户名"
        >
          <a-input
            v-model:value="formState.username"
            size="large"
            placeholder="请输入用户名"
            allow-clear
          >
            <template #prefix>
              <UserOutlined style="color: rgba(0, 0, 0, 0.25)" />
            </template>
          </a-input>
        </a-form-item>
        <a-form-item
          name="password"
          label="密码"
        >
          <a-input-password
            v-model:value="formState.password"
            size="large"
            placeholder="请输入密码"
          >
            <template #prefix>
              <LockOutlined style="color: rgba(0, 0, 0, 0.25)" />
            </template>
          </a-input-password>
        </a-form-item>
        <a-form-item>
          <a-button
            type="primary"
            html-type="submit"
            size="large"
            block
            :loading="loading"
          >
            登录
          </a-button>
        </a-form-item>
      </a-form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { UserOutlined, LockOutlined } from "@ant-design/icons-vue";
import LottieIcon from "@/components/LottieIcon.vue";
import runOxAnimation from "@/assets/RunOx.json";
import { authStore } from "@/store/auth";
import { postJson } from "@/utils/request";
import { hasMenu } from "@/utils/permission";

const router = useRouter();
const formRef = ref();
const loading = ref(false);

const formState = reactive({
  username: "",
  password: "",
});

const rules = {
  username: [{ required: true, message: "请输入用户名", trigger: "blur" }],
  password: [{ required: true, message: "请输入密码", trigger: "blur" }],
};

/** 登录成功后跳转到第一个有权限的菜单 */
const getFirstAllowedRoute = () => {
  const allRoutes = [
    "/dashboard",
    "/order/daily",
    "/order/app-daily",
    "/order/hourly-payment",
    "/order/link-stats",
    "/order/customer-service-stats",
    "/order/hlb-service-transfer-stats",
    "/order/app-monthly",
    "/order/ltv",
    "/order/ltv-v2",
    "/order/pay-refund-daily",
    "/order/daily-stat",
    "/order/deduction-tag-daily",
    "/order/deduction-forecast-v2",
    "/system/user",
    "/system/role",
  ];
  return allRoutes.find((r) => hasMenu(r)) || "/dashboard";
};

const handleLogin = async () => {
  loading.value = true;
  try {
    const json = await postJson("/v1/analytics/auth/login", {
      username: formState.username,
      password: formState.password,
    });
    if (json.code === 200 && json.data) {
      authStore.setAuth(json.data.token, json.data.user);
      message.success("登录成功");
      router.push(getFirstAllowedRoute());
    } else {
      message.error(json.content || "用户名或密码错误");
    }
  } catch {
    message.error("登录请求失败");
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fb 0%, #eceef2 100%);
}

.login-box {
  width: 400px;
  padding: 40px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid #eceef2;
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-lottie-wrap {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  margin-bottom: 10px;
}

.login-lottie-wrap :deep(.lottie-icon-wrap) {
  transform: scale(3.5);
  transform-origin: center;
}

.login-title {
  font-size: 20px;
  font-weight: 600;
  color: #333333;
  margin: 0;
}
</style>
